import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import React from 'react';
import { create } from 'zustand';
import * as enabledApi from '../../api/enabledPlayApi';
import {
    ENABLED_ACCESS_EXPIRATION,
    ENABLED_ACCESS_TOKEN_STORE,
    ENABLED_REFRESH_TOKEN_STORE,
    getAuthClientType,
    getEnabledClientId,
    getEnabledClientSecret,
} from '../../constants/Keys';
import { getAuthRedirectUri } from '../../constants/Urls';
import TokenResponse from '../../models/authentication/TokenResponse';
import IResult from '../../models/result/IResult';

type AuthListener = () => void;

const signoutListeners: AuthListener[] = [];

interface AuthState {
    accessToken: string | null;
    errors: string[];
    expirationDate: Date;
    isAuthenticated: boolean;
    isInitialized: boolean;
    isLoading: boolean;
    isRefreshing: boolean;
    isLoadingDataComplete: boolean;
    refreshToken: string | null;

    addSignoutListener: (listener: AuthListener) => void;
    getNewToken: () => Promise<IResult<TokenResponse>>;
    handleIsLoading: (loading: boolean) => void;
    initializeFromStorage: () => boolean;
    loginFromCode: (code: string) => Promise<IResult<TokenResponse>>;
    logout: () => void;
    removeSignoutListener: (listener: AuthListener) => void;
    setIsInitialized: (value: boolean) => void;
    setIsLoadingDataComplete: (value: boolean) => void;
}

const notifyListeners = (listeners: AuthListener[]) => {
    listeners.forEach((listener) => {
        try {
            listener();
        } catch (error) {
            console.error('Error in signout listener:', error);
        }
    });
};

export const useAuthStore = create<AuthState>()((set, get) => {
    const setIsInitialized = (value: boolean) => set({ isInitialized: value });

    const setIsLoadingDataComplete = (value: boolean) => set({ isLoadingDataComplete: value });

    const handleIsLoading = (loading: boolean) => set({ isLoading: loading });

    const initializeFromStorage = (): boolean => {
        set({ isLoading: true });
        try {
            const accessToken = sessionStorage.getItem(ENABLED_ACCESS_TOKEN_STORE);
            const refreshToken = localStorage.getItem(ENABLED_REFRESH_TOKEN_STORE);
            const expirationDate = localStorage.getItem(ENABLED_ACCESS_EXPIRATION);
            if (refreshToken) {
                set({
                    accessToken,
                    refreshToken,
                    isAuthenticated: true,
                    isInitialized: true,
                    ...(expirationDate && { expirationDate: new Date(expirationDate) }),
                });
                console.log('Auth.initializeFromStorage: Authenticated');
            } else {
                console.log('Auth.initializeFromStorage: No refresh token found');
            }
            return true;
        } catch (e) {
            console.log('Auth.initializeFromStorage:', e);
            return false;
        } finally {
            set({ isLoading: false });
        }
    };

    const loginFromCode = async (code: string): Promise<IResult<TokenResponse>> => {
        set({ isLoading: true });
        try {
            const result = await enabledApi.getToken(getEnabledClientId(), code, getAuthRedirectUri());
            if (result?.resultType === 'Ok' && result.data) {
                sessionStorage.setItem(ENABLED_ACCESS_TOKEN_STORE, result.data.access_token);
                localStorage.setItem(ENABLED_REFRESH_TOKEN_STORE, result.data.refresh_token);
                localStorage.setItem(ENABLED_ACCESS_EXPIRATION, result.data.access_token_expiration);
                set({
                    accessToken: result.data.access_token,
                    refreshToken: result.data.refresh_token,
                    expirationDate: new Date(result.data.access_token_expiration),
                    isAuthenticated: true,
                    isInitialized: true,
                });
            } else if (result.errors?.length) {
                set({ errors: [...result.errors] });
            }
            return result;
        } catch (error) {
            set({ errors: [error] });
            return new UnexpectedResult<TokenResponse>();
        } finally {
            set({ isLoading: false });
        }
    };

    const getNewToken = async (): Promise<IResult<TokenResponse>> => {
        set({ isRefreshing: true });
        try {
            const latestRefresh = localStorage.getItem(ENABLED_REFRESH_TOKEN_STORE);
            if (!latestRefresh) {
                set({ isRefreshing: false });
                return { resultType: 'Invalid', errors: ['No refresh token found'] };
            }
            const result = await enabledApi.refreshToken(getEnabledClientId(), getEnabledClientSecret(), latestRefresh);

            // desktop app can use local storage, admin app (web app) should reject
            const isDesktop = getAuthClientType() == 'desktop';
            if (isDesktop && (result.resultType === 'Offline' || result.resultType === 'NoResponse')) {
                const storage = get().initializeFromStorage();
                if (storage) return { resultType: 'Ok' };
            }
            if (result?.resultType === 'Ok' && result.data) {
                sessionStorage.setItem(ENABLED_ACCESS_TOKEN_STORE, result.data.access_token);
                localStorage.setItem(ENABLED_REFRESH_TOKEN_STORE, result.data.refresh_token);
                localStorage.setItem(ENABLED_ACCESS_EXPIRATION, result.data.access_token_expiration);
                set({
                    accessToken: result.data.access_token,
                    refreshToken: result.data.refresh_token,
                    expirationDate: new Date(result.data.access_token_expiration),
                    isAuthenticated: true,
                    isInitialized: true,
                });
            }
            if (result.errors?.length && result.resultType !== 'Ok') {
                set({ errors: [...result.errors] });
                get().logout();
            }
            return result;
        } catch (error) {
            set({ errors: [error] });
            return { resultType: 'Invalid', errors: [error] };
        } finally {
            set({ isRefreshing: false });
        }
    };

    const logout = (): void => {
        set({ isLoading: true });
        notifyListeners(signoutListeners);
        sessionStorage.clear();
        localStorage.clear();
        signoutListeners.length = 0;
        set({
            isAuthenticated: false,
            accessToken: null,
            refreshToken: null,
            expirationDate: new Date(),
            isLoading: false,
        });
    };

    const addSignoutListener = (listener: AuthListener) => signoutListeners.push(listener);

    const removeSignoutListener = (listener: AuthListener) => {
        const idx = signoutListeners.indexOf(listener);
        if (idx !== -1) signoutListeners.splice(idx, 1);
    };

    return {
        // State
        accessToken: null,
        errors: [],
        expirationDate: new Date(),
        isAuthenticated: false,
        isInitialized: false,
        isLoading: false,
        isRefreshing: false,
        isLoadingDataComplete: false,
        refreshToken: null,

        // Actions
        addSignoutListener,
        getNewToken,
        handleIsLoading,
        initializeFromStorage,
        loginFromCode,
        logout,
        removeSignoutListener,
        setIsInitialized,
        setIsLoadingDataComplete,
    };
});
